// M1.5 smoke test — save an email draft to the signed-in user's Outlook Drafts.
//
// Form is pre-filled with the McMaster reply draft so the first thing Justin
// tests is the McMaster reply landing in his actual Outlook. Win.
//
// After this surface works, Brain.compose() wires to call this same Graph helper.

import { useEffect, useState } from 'react';
import { Brain } from '@/sdk/brain';
import { saveToOutlookDrafts } from '@/shared/graph';
import { ensureMsalInitialized, getActiveAccount } from '@/shared/auth';
import { msalConfigured } from '@/shared/msalConfig';
import type { BrainError } from '@/sdk/brain';

const MCMASTER_TO = 'eProcurement@mcmaster.com';
const MCMASTER_SUBJECT = 'RE: Product Information API access request — Keystone Machine & Fab';
const MCMASTER_BODY = `Hi Steven,

Signed agreement attached.

The dedicated API login is set up at [INSERT NEW ACCOUNT EMAIL] — 2FA disabled per your requirement, and linked to the KMF customer account.

Phone is 440-865-0363 as confirmed — please use that line for the certificate passcode.

I'll be ready to deploy as soon as the cert arrives. Thanks for the quick turnaround.

Justin Jenkins
Keystone Machine & Fab
justin@keymf.com
440-865-0363`;

export function OutlookDraftTest() {
  const [to, setTo] = useState(MCMASTER_TO);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(MCMASTER_SUBJECT);
  const [body, setBody] = useState(MCMASTER_BODY);
  const [signedIn, setSignedIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [composing, setComposing] = useState(false);
  const [result, setResult] = useState<{ id: string; webLink: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jemmaPrompt, setJemmaPrompt] = useState(
    'Reply to Steven at McMaster confirming the agreement is signed and the dedicated API account is set up at mcmaster-api@keymf.com (2FA disabled, linked to KMF customer account). Phone for cert passcode is 440-865-0363. Thank him for the quick turnaround.',
  );
  const [jemmaUsage, setJemmaUsage] = useState<{ tokensIn: number; tokensOut: number; latencyMs: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await ensureMsalInitialized();
      if (cancelled || !ok) return;
      setSignedIn(getActiveAccount() !== null);
      // Poll every second for sign-in state changes — cheap, dev-only convenience
      const id = setInterval(() => {
        if (cancelled) return;
        setSignedIn(getActiveAccount() !== null);
      }, 1000);
      return () => clearInterval(id);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const draft = await saveToOutlookDrafts({
        to,
        cc: cc || undefined,
        subject,
        body,
        bodyType: 'Text',
      });
      setResult({ id: draft.id, webLink: draft.webLink });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleJemmaCompose() {
    if (!jemmaPrompt.trim()) {
      setError('Tell Jemma what to write first.');
      return;
    }
    setComposing(true);
    setError(null);
    setResult(null);
    setJemmaUsage(null);
    try {
      const composed = await Brain.compose({
        task: 'generic',
        input: jemmaPrompt,
        saveTo: 'in_app_only', // we want to show the result first, let Justin review, then save
      });
      setSubject(composed.subject ?? '');
      setBody(composed.body);
      setJemmaUsage({
        tokensIn: composed.usage.tokensIn,
        tokensOut: composed.usage.tokensOut,
        latencyMs: composed.usage.latencyMs,
      });
    } catch (e) {
      const be = e as BrainError;
      setError(`Compose failed (${be.type ?? 'unknown'}): ${be.message ?? String(e)}`);
    } finally {
      setComposing(false);
    }
  }

  async function handleJemmaComposeAndSave() {
    if (!jemmaPrompt.trim()) {
      setError('Tell Jemma what to write first.');
      return;
    }
    if (!signedIn) {
      setError('Sign in with Microsoft first (header).');
      return;
    }
    setComposing(true);
    setError(null);
    setResult(null);
    setJemmaUsage(null);
    try {
      const composed = await Brain.compose({
        task: 'generic',
        input: jemmaPrompt,
        saveTo: 'outlook_drafts',
        destination: { to, cc: cc || undefined },
      });
      setSubject(composed.subject ?? '');
      setBody(composed.body);
      setJemmaUsage({
        tokensIn: composed.usage.tokensIn,
        tokensOut: composed.usage.tokensOut,
        latencyMs: composed.usage.latencyMs,
      });
      if (composed.draftId) {
        setResult({ id: composed.draftId, webLink: `https://outlook.office.com/mail/drafts/id/${encodeURIComponent(composed.draftId)}` });
      }
    } catch (e) {
      const be = e as BrainError;
      setError(`Compose+save failed (${be.type ?? 'unknown'}): ${be.message ?? String(e)}`);
    } finally {
      setComposing(false);
    }
  }

  if (!msalConfigured) {
    return (
      <div className="bg-paper border border-line rounded p-4">
        <h2 className="font-semibold text-navy mb-1">Outlook draft (M1.5)</h2>
        <p className="text-sm text-text-mute">
          MS Graph not configured. Set <code className="bg-bg px-1 rounded">VITE_MSAL_CLIENT_ID</code> +{' '}
          <code className="bg-bg px-1 rounded">VITE_MSAL_TENANT_ID</code> in{' '}
          <code className="bg-bg px-1 rounded">.env.local</code>. See{' '}
          <code className="bg-bg px-1 rounded">src/shared/msalConfig.ts</code> for the Azure setup steps.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-paper border border-line rounded p-4">
      <h2 className="font-semibold text-navy mb-1">Outlook draft (M1.5)</h2>
      <p className="text-xs text-text-mute mb-4">
        Pre-filled with the McMaster reply. Sign in with Microsoft (header), then click{' '}
        <strong>Save to Outlook Drafts</strong>. Per the hard rule — this only drafts. You review and send from Outlook.
      </p>

      {!signedIn && (
        <div className="mb-3 bg-amber/10 border-l-4 border-amber text-text px-3 py-2 text-sm">
          ⚠ Not signed in. Click <strong>Sign in with Microsoft</strong> in the header first.
        </div>
      )}

      <div className="mb-4 border border-orange/30 bg-orange/5 rounded p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-orange font-bold">●</span>
          <span className="font-semibold text-sm">Have Jemma compose this</span>
          {jemmaUsage && (
            <span className="text-xs text-text-mute font-mono ml-auto">
              {jemmaUsage.tokensIn} in / {jemmaUsage.tokensOut} out · {jemmaUsage.latencyMs}ms
            </span>
          )}
        </div>
        <textarea
          value={jemmaPrompt}
          onChange={(e) => setJemmaPrompt(e.target.value)}
          rows={3}
          placeholder="Tell Jemma what to write..."
          className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange mb-2"
        />
        <div className="flex gap-2">
          <button
            onClick={handleJemmaCompose}
            disabled={composing || !jemmaPrompt.trim()}
            className="px-3 py-1 bg-paper border border-orange text-orange text-sm rounded font-semibold hover:bg-orange/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {composing ? 'Composing…' : '✏ Compose only'}
          </button>
          <button
            onClick={handleJemmaComposeAndSave}
            disabled={composing || !jemmaPrompt.trim() || !signedIn}
            className="px-3 py-1 bg-orange text-white text-sm rounded font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            title={!signedIn ? 'Sign in with Microsoft first' : 'Compose + save to Outlook Drafts in one step'}
          >
            {composing ? 'Working…' : '✨ Compose + save to Drafts'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">To</label>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="someone@example.com"
            className="w-full px-2 py-1 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </div>
        <div>
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">CC (optional)</label>
          <input
            type="text"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            placeholder="(optional)"
            className="w-full px-2 py-1 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="w-full px-2 py-1 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={busy || !signedIn || (!subject && !body)}
          className="px-4 py-2 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
        >
          {busy ? 'Saving…' : 'Save to Outlook Drafts'}
        </button>
        {result && (
          <a
            href={result.webLink}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue hover:underline"
          >
            Open draft in Outlook ↗
          </a>
        )}
      </div>

      {error && (
        <div className="mt-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
          ⚠ {error}
        </div>
      )}

      {result && (
        <div className="mt-3 bg-green/10 border-l-4 border-green text-text px-3 py-2 text-sm">
          ✓ Saved to Outlook Drafts. Draft ID: <code className="font-mono text-xs">{result.id.slice(0, 24)}…</code>
        </div>
      )}
    </div>
  );
}
